import { computed, onBeforeUnmount, ref, toValue, watch } from 'vue';

export const MAX_IMAGE_RETRIES = 2;
export const IMAGE_RETRY_DELAYS = [1000, 2000];

const ACTIVE_STORAGE_PATH = '/rails/active_storage/';

// Our own attachment urls are 302s to a pre-signed S3 url, and the browser caches that redirect
// (Cache-Control:max-age, see config/initializers/active_storage_redirect_cache.rb). Retrying the
// exact same url would replay the cached redirect -- and with it the same, possibly already dead,
// S3 target -- so a retry only means anything with a cache buster on it. Foreign hosts
// (attachment.external_url points at the Facebook/Instagram CDN) carry a signature of their own,
// so we leave their query string alone and lean on the element remount instead.
const isOwnAttachmentUrl = url => {
  if (!url) return false;

  try {
    const { origin, pathname } = new URL(url, window.location.origin);
    return (
      origin === window.location.origin &&
      pathname.startsWith(ACTIVE_STORAGE_PATH)
    );
  } catch {
    return false;
  }
};

const withRetryParam = (url, attempt) => {
  if (!attempt || !isOwnAttachmentUrl(url)) return url;

  return `${url}${url.includes('?') ? '&' : '?'}cwRetry=${attempt}`;
};

/**
 * Keeps an <img> alive across transient load failures.
 *
 * A single `error` event used to be final: the component flipped to "loading failed" and never
 * asked for the image again, so one hiccup on the way to S3 left the attachment dead until the
 * agent reloaded the page (task 6168).
 *
 * @param {import('vue').MaybeRefOrGetter<string>} source - the attachment url to render
 * @returns {{
 *   src: import('vue').ComputedRef<string>,
 *   attempt: import('vue').Ref<number>,
 *   hasError: import('vue').Ref<boolean>,
 *   isRetrying: import('vue').Ref<boolean>,
 *   onError: () => void,
 *   markFailed: () => void,
 *   reset: () => void,
 * }} `attempt` is meant to be bound as the element's `:key` so each retry mounts a fresh element.
 */
export function useRetryableImage(source) {
  const attempt = ref(0);
  const hasError = ref(false);
  const isRetrying = ref(false);

  let retryTimer = null;

  const clearRetryTimer = () => {
    if (!retryTimer) return;

    clearTimeout(retryTimer);
    retryTimer = null;
  };

  const src = computed(() => withRetryParam(toValue(source), attempt.value));

  const reset = () => {
    clearRetryTimer();
    attempt.value = 0;
    hasError.value = false;
    isRetrying.value = false;
  };

  const markFailed = () => {
    clearRetryTimer();
    isRetrying.value = false;
    hasError.value = true;
  };

  const onError = () => {
    if (attempt.value >= MAX_IMAGE_RETRIES) {
      markFailed();
      return;
    }

    isRetrying.value = true;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      isRetrying.value = false;
      attempt.value += 1;
    }, IMAGE_RETRY_DELAYS[attempt.value]);
  };

  watch(() => toValue(source), reset);
  onBeforeUnmount(clearRetryTimer);

  return { src, attempt, hasError, isRetrying, onError, markFailed, reset };
}
